import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseconfig';
import { hasPermission, USER_ROLES } from '../utils/roles';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser({ ...user, ...userData });
            setUserRole(userData.role);
          } else {
            const defaultUserData = {
              uid: user.uid,
              email: user.email,
              role: USER_ROLES.USER,
              createdAt: new Date().toISOString()
            };
            await setDoc(doc(db, 'users', user.uid), defaultUserData);
            setCurrentUser({ ...user, ...defaultUserData });
            setUserRole(USER_ROLES.USER);
          }
        } else {
          setCurrentUser(null);
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setCurrentUser(null);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true); // prevent route guards from redirecting during first login
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signup = async (email, password, name) => {
    try {
      setLoading(true); // keep guards in loading state until auth observer updates
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', result.user.uid), {
        name,
        email,
        role: USER_ROLES.USER,
        createdAt: new Date().toISOString(),
        status: 'active'
      });
      return result.user;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const createNewUser = async (email, password, name, role) => {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name,
        email,
        role,
        createdAt: new Date().toISOString(),
        status: 'active',
        uid: userCredential.user.uid
      });
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser(prevUser => ({ ...prevUser, ...userData }));
          setUserRole(userData.role);
        }
      }
      return userCredential.user;
    } catch (error) {
      console.error('Error in createNewUser:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const checkPermission = (permission) => {
    return hasPermission(userRole, permission);
  };

  const value = {
    currentUser,
    userRole,
    loading,
    login,
    signup,
    logout,
    checkPermission,
    createNewUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      )}
    </AuthContext.Provider>
  );
}; 